// src/RiskManagerToolkit.js
import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Tabs, Card, Text, Box, Button, Stack, Alert, Group } from '@mantine/core';
import { IconTimeline, IconMap2, IconShieldCheck, IconCircleCheck, IconAlertCircle } from '@tabler/icons-react';
import StageMonitor from './StageMonitor';
import ScenarioAnalyzer from './NarrativeScenarioAnalyzer';
import Compliance from './Compliance';
import { approveReview } from './redux/actions/reviewActions';

/**
 * Risk Manager Toolkit Component
 *
 * A tabbed interface that provides Risk Managers with tools for reviewing
 * and analyzing completed assessments:
 * - Model Lifecycle Analyzer: Analyze how model stage changes impact risks
 * - Use Case Scenario Analyzer: Analyze how context changes impact risks
 * - Compliance Analyzer: Perform compliance gap analysis
 *
 * @param {Object} props
 * @param {string} props.modelId - The ID of the model
 * @param {string} props.assessmentUuid - The UUID of the assessment
 * @param {Object} props.riskFormData - The risk form data
 * @param {Object} props.narratives - The narratives for context sections
 */
export default function RiskManagerToolkit({ modelId, assessmentUuid, riskFormData, narratives }) {
  const dispatch = useDispatch();
  const [approvalSuccess, setApprovalSuccess] = useState(false);
  const [approvalError, setApprovalError] = useState(null);

  // Get review data from reviews Redux state
  const reviewData = useSelector((state) => state.reviews.byModel[modelId] || {});
  const reviewStatus = reviewData.reviewStatus || null;
  const reviewLoading = useSelector((state) => state.reviews.loading);

  // Handle Approve Review
  const handleApproveReview = async () => {
    setApprovalError(null);
    try {
      await dispatch(approveReview(modelId, assessmentUuid, ''));
      setApprovalSuccess(true);
      console.log('[RiskManagerToolkit] Review approved successfully');
    } catch (error) {
      setApprovalError('An error occurred while approving the review.');
      console.error('[RiskManagerToolkit] Error approving review:', error);
    }
  };

  return (
    <Card withBorder radius="md" p="lg">
      <Stack gap="lg">
        <Box>
          <Text size="xl" fw={700}>Risk Manager Toolkit</Text>
          <Text size="sm" c="dimmed" mt="xs">
            Advanced tools for reviewing and analyzing completed assessments
          </Text>
        </Box>

        {/* Approval Success Message */}
        {approvalSuccess && (
          <Alert color="green" title="Review Approved" icon={<IconCircleCheck size={16} />}>
            The assessment review has been approved successfully. The user will be notified.
          </Alert>
        )}

        {/* Approval Error Message */}
        {approvalError && (
          <Alert color="red" title="Approval Failed" icon={<IconAlertCircle size={16} />}>
            {approvalError}
          </Alert>
        )}

        {/* Approve Review Button */}
        {reviewStatus === 'submitted' && !approvalSuccess && (
          <Card withBorder p="md" bg="green.0">
            <Group justify="space-between" align="center">
              <Box>
                <Text size="md" fw={600} c="green.9">
                  Ready to approve this assessment?
                </Text>
                <Text size="sm" c="dimmed" mt={4}>
                  Use the analyzers below to review the assessment, then click approve when satisfied.
                </Text>
              </Box>
              <Button
                size="lg"
                color="green"
                leftSection={<IconCircleCheck size={18} />}
                onClick={handleApproveReview}
                loading={reviewLoading}
              >
                Approve Review
              </Button>
            </Group>
          </Card>
        )}

        <Tabs defaultValue="lifecycle" keepMounted={false}>
          <Tabs.List>
            <Tabs.Tab value="lifecycle" leftSection={<IconTimeline size={16} />}>
              Lifecycle Analyzer
            </Tabs.Tab>
            <Tabs.Tab value="scenario" leftSection={<IconMap2 size={16} />}>
              Scenario Analyzer
            </Tabs.Tab>
            <Tabs.Tab value="compliance" leftSection={<IconShieldCheck size={16} />}>
              Compliance Analyzer
            </Tabs.Tab>
          </Tabs.List>

        <Tabs.Panel value="lifecycle" pt="lg">
          <Card withBorder radius="md" p="md">
            <Text size="lg" fw={600} mb="xs">Model Lifecycle Analyzer</Text>
            <Text size="sm" c="dimmed" mb="md">
              Review and update model risks and action plans based on model progress across the model lifecycle
            </Text>
            <StageMonitor modelId={modelId} />
          </Card>
        </Tabs.Panel>

        <Tabs.Panel value="scenario" pt="lg">
          <Card withBorder radius="md" p="md">
            <Text size="lg" fw={600} mb="xs">Use Case Scenario Analyzer</Text>
            <Text size="sm" c="dimmed" mb="md">
              Inspects how changes to the use case, business and model context affects current risks and action plans
            </Text>
            <ScenarioAnalyzer modelId={modelId} />
          </Card>
        </Tabs.Panel>

        <Tabs.Panel value="compliance" pt="lg">
          <Compliance
            modelId={modelId}
            assessmentUuid={assessmentUuid}
            riskFormData={riskFormData}
            narratives={narratives}
          />
        </Tabs.Panel>
      </Tabs>
      </Stack>
    </Card>
  );
}
